import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box, Button, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress,
  Chip, IconButton, Alert, Tabs, Tab, Card, CardContent,
  Divider, Snackbar, Stack, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, FormGroup, FormControlLabel,
  Checkbox, TextField, MenuItem, Pagination, Collapse,
  ToggleButton, ToggleButtonGroup, Tooltip,
} from "@mui/material";
import {
  Add, Edit, Refresh, ArrowBack, CheckCircle, RadioButtonUnchecked,
  NotificationsOffOutlined, NotificationsActiveOutlined, RefreshOutlined,
  CheckCircleOutlined, ErrorOutlineOutlined, HourglassEmptyOutlined,
  BlockOutlined, LockOpenOutlined, FilterListOutlined,
  KeyboardArrowDown, KeyboardArrowUp, WarningAmberOutlined,
  NotificationsNoneOutlined, InfoOutlined,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { gqlRequest, GET_BUSINESS_USAGES } from "../../api/graphql";
import { useAuth } from "../../store/AuthContext";
import { fmtDate } from "../../utils/helpers";
import { COLORS } from "../../theme";
import {
  PageWrapper, PageHeader, StatusChip, PlanChip, BizAvatar,
  SectionCard, EmptyState,
} from "../../components/UI";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtTimestamp = (val) => {
  if (val === null || val === undefined || val === "") return null;
  const ms = typeof val === "number" ? val : parseInt(val, 10);
  if (Number.isNaN(ms)) return null;
  return fmtDate(ms);
};

const isUsageActive = (enddate) => {
  if (enddate === null || enddate === undefined || enddate === "") return true;
  const ms = typeof enddate === "number" ? enddate : parseInt(enddate, 10);
  if (Number.isNaN(ms)) return true;
  return ms >= Date.now();
};

const fmtDt = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return v; }
};

const PAYMENT_BASE = process.env.REACT_APP_PAYMENTURL;

// ─── Feature columns ──────────────────────────────────────────────────────────

const FEATURE_FIELDS = [
  { label: "Users",           limitKey: "userslimit",          unlimitedKey: "isunlimitedusers" },
  { label: "Email",           limitKey: "emaillimit",          unlimitedKey: "isunlimitedemail" },
  { label: "SMS",             limitKey: "textsmslimit",        unlimitedKey: "isunlimitedtextsms" },
  { label: "Storage",         limitKey: "storagelimit",        unlimitedKey: "isunlimitedstorage" },
  { label: "Call Time",       limitKey: "calltimelimit",       unlimitedKey: "isunlimitedcalltime" },
  { label: "Contacts",        limitKey: "leadgatheringlimit",  unlimitedKey: "isunlimitedleadgathering" },
  { label: "Social Post",     limitKey: "socialpostlimit",     unlimitedKey: "isunlimitedsocialpost" },
  { label: "Email Finder",    limitKey: "emailfinderlimit",    unlimitedKey: "isunlimitedemailfinder" },
  { label: "Lead Finder",     limitKey: "leadfinderlimit",     unlimitedKey: "isunlimitedleadfinder" },
  { label: "AI Query",        limitKey: "aiquerylimit",        unlimitedKey: "isunlimitedaiquery" },
  { label: "LinkedIn Users",  limitKey: "linkedinuserlimit",   unlimitedKey: null },
  { label: "WhatsApp Users",  limitKey: "whatsappuserlimit",   unlimitedKey: null },
  { label: "Instagram Users", limitKey: "instagramuserlimit",  unlimitedKey: null },
  { label: "Enrich Search",   limitKey: "enrichsearchlimit",   unlimitedKey: null },
  { label: "Enrich Reveal",   limitKey: "enrichreveallimit",   unlimitedKey: null },
];

const TABLE_MAX_HEIGHT = 520;

// ─── Interval helpers ─────────────────────────────────────────────────────────

const intervalLabel = (v) => {
  if (!v) return "";
  if (v === "ONE_MONTH")    return "Monthly";
  if (v === "TWELVE_MONTH") return "Yearly";
  if (v === "SIX_MONTH")    return "6 Months";
  if (v === "THREE_MONTH")  return "3 Months";
  return v.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

const intervalColor = (v) => {
  if (v === "ONE_MONTH")    return { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" };
  if (v === "TWELVE_MONTH") return { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" };
  return { bg: "#f8fafc", color: "#64748b", border: "#e2e8f0" };
};

// ─── Expiry notification constants ────────────────────────────────────────────

const STAGES   = ["EXPIRING_48H", "EXPIRED", "EXPIRED_24H"];
const STATUSES = ["SENT", "FAILED", "PENDING", "SKIPPED"];
const PAGE_SIZE_OPTIONS = [10, 25, 50];

const STAGE_META = {
  EXPIRING_48H: { label: "Expiring 48h", color: "warning" },
  EXPIRED:      { label: "Expired",       color: "error"   },
  EXPIRED_24H:  { label: "Expired 24h",   color: "error"   },
};
const STATUS_META = {
  SENT:    { label: "Sent",    color: "success", Icon: CheckCircleOutlined   },
  FAILED:  { label: "Failed",  color: "error",   Icon: ErrorOutlineOutlined  },
  PENDING: { label: "Pending", color: "warning", Icon: HourglassEmptyOutlined },
  SKIPPED: { label: "Skipped", color: "default", Icon: BlockOutlined         },
};

// ─── GraphQL ──────────────────────────────────────────────────────────────────

// Exact field selections confirmed against the live schema
const EXCLUSION_RULE_FIELDS = `
  exclusionid
  businessid
  businessname
  email
  emaildomain
  stage
  reason
  source
  expiresat
  isactive
  createdby
  createdat
`;

const EXCLUSION_STATE_FIELDS = `
  businessid
  isexcluded
  excludedstages
  rules {
    ${EXCLUSION_RULE_FIELDS}
  }
  cleared
`;

// 1. Notification log
const GQL_NOTIFICATIONS = `
  query PlanExpiryNotifications($filter: PlanExpiryNotificationFilter) {
    planExpiryNotifications(filter: $filter) {
      count
      pagenumber
      pagesize
      rows {
        notificationid
        businessid
        businessname
        brandname
        email
        stage
        status
        planenddate
        sentat
        errormessage
        createdat
      }
    }
  }
`;

// 2. Exclusion state query
const GQL_EXCLUSION_STATE = `
  query PlanExpiryBusinessExclusionState($businessid: ID!) {
    planExpiryBusinessExclusionState(businessid: $businessid) {
      ${EXCLUSION_STATE_FIELDS}
    }
  }
`;

// 3. Exclude mutation — input: { businessid, stages?, reason?, expiresat? }
const GQL_EXCLUDE = `
  mutation ExcludeBusinessFromPlanExpiry($input: PlanExpiryExcludeBusinessInput!) {
    excludeBusinessFromPlanExpiry(input: $input) {
      ${EXCLUSION_STATE_FIELDS}
    }
  }
`;

// 4. Include (re-enable) mutation — input: businessid (string, not object)
const GQL_INCLUDE = `
  mutation IncludeBusinessInPlanExpiry($businessid: ID!) {
    includeBusinessInPlanExpiry(businessid: $businessid) {
      ${EXCLUSION_STATE_FIELDS}
    }
  }
`;

// ═════════════════════════════════════════════════════════════════════════════
// PlanCard
// ═════════════════════════════════════════════════════════════════════════════

function PlanCard({ plan, businessid, onAssign, onUnassign, assigning, showActions = true }) {
  const planId      = plan._planId;
  const name        = plan.name || `Plan ${planId}`;
  const price       = plan.price;
  const strikePrice = plan.strikePrice || plan.strikeprice;
  const interval    = plan.planInterval || plan.planinterval;
  const busy        = assigning === planId;

  const bids  = Array.isArray(plan.businessids) ? plan.businessids : [];
  const bsubs = Array.isArray(plan.businessSubscriptions) ? plan.businessSubscriptions : [];
  const isAssigned = bids.includes(businessid) ||
    bsubs.some((s) => s.businessid === businessid && s.status !== "CANCELLED");

  const rawFeatures = Array.isArray(plan.planfeatures) ? plan.planfeatures : [];
  const features = rawFeatures
    .filter((f) => f.features?.name)
    .map((f) => ({ name: f.features.name, unlimited: !!f.isunlimited, value: f.allownumber }));

  const ic = intervalColor(interval);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: isAssigned ? "#6366f1" : COLORS.border,
        borderWidth: isAssigned ? 2 : 1,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow .2s, border-color .2s",
        boxShadow: isAssigned
          ? "0 0 0 4px rgba(99,102,241,0.10)"
          : "0 1px 3px rgba(0,0,0,0.06)",
        "&:hover": {
          boxShadow: isAssigned
            ? "0 0 0 4px rgba(99,102,241,0.15)"
            : "0 4px 12px rgba(0,0,0,0.10)",
        },
      }}
    >
      {isAssigned && (
        <Box sx={{
          position: "absolute", top: 0, right: 0,
          bgcolor: "#6366f1", color: "#fff",
          fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5,
          px: 1.5, py: 0.4,
          borderRadius: "0 12px 0 8px",
          display: "flex", alignItems: "center", gap: 0.4,
        }}>
          <CheckCircle sx={{ fontSize: 10 }} /> ASSIGNED
        </Box>
      )}

      <CardContent sx={{ flex: 1, pt: 2, pb: "0 !important" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5, flexWrap: "wrap" }}>
          {interval && (
            <Chip size="small" label={intervalLabel(interval)} sx={{
              fontSize: 10, fontWeight: 700, height: 20,
              bgcolor: ic.bg, color: ic.color, border: `1px solid ${ic.border}`,
            }} />
          )}
        </Box>

        <Typography sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.25, mb: 0.75, pr: isAssigned ? 4 : 0 }}>
          {name}
        </Typography>

        {plan.shortdescription && plan.shortdescription !== name && (
          <Typography sx={{ fontSize: 11.5, color: COLORS.muted, mb: 1 }}>
            {plan.shortdescription}
          </Typography>
        )}

        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mb: 2 }}>
          <Typography sx={{ fontSize: 26, fontWeight: 900, color: "#1e1b4b", lineHeight: 1 }}>
            ${parseFloat(price || 0).toFixed(2)}
          </Typography>
          {strikePrice && parseFloat(strikePrice) > parseFloat(price || 0) && (
            <Typography sx={{ fontSize: 13, color: COLORS.muted, textDecoration: "line-through" }}>
              ${parseFloat(strikePrice).toFixed(2)}
            </Typography>
          )}
          {interval && (
            <Typography sx={{ fontSize: 12, color: COLORS.muted, alignSelf: "flex-end", mb: 0.2 }}>
              / {intervalLabel(interval).toLowerCase()}
            </Typography>
          )}
        </Box>

        {features.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: COLORS.muted, mb: 0.75 }}>
              Includes
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {features.map((f, i) => (
                <Chip key={i} size="small"
                  label={f.unlimited ? `∞ ${f.name}` : `${f.value ? f.value.toLocaleString() : "0"} ${f.name}`}
                  sx={{
                    fontSize: 10.5, fontWeight: 600, height: 22,
                    bgcolor: f.unlimited ? "#f0fdf4" : "#f8fafc",
                    border: `1px solid ${f.unlimited ? "#bbf7d0" : COLORS.border}`,
                    color: f.unlimited ? "#15803d" : "#374151",
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>

      <Box sx={{ px: 2, pb: 2, pt: 1.5 }}>
        <Divider sx={{ mb: 1.5 }} />
        {!showActions ? (
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75,
            py: 0.9, borderRadius: 2,
            bgcolor: isAssigned ? "#f0fdf4" : "#f8fafc",
            border: `1px solid ${isAssigned ? "#bbf7d0" : COLORS.border}`,
          }}>
            {isAssigned ? (
              <>
                <CheckCircle sx={{ fontSize: 15, color: "#16a34a" }} />
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#15803d" }}>Active Plan</Typography>
              </>
            ) : (
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: COLORS.muted }}>Not Assigned</Typography>
            )}
          </Box>
        ) : isAssigned ? (
          <Button
            fullWidth variant="outlined" size="small" disabled={busy}
            onClick={() => onUnassign(plan)}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: 2, py: 0.9,
              borderColor: "#fca5a5", color: "#dc2626",
              "&:hover": { bgcolor: "#fef2f2", borderColor: "#ef4444" },
            }}
            startIcon={busy ? <CircularProgress size={13} /> : <RadioButtonUnchecked sx={{ fontSize: 16 }} />}
          >
            {busy ? "Removing…" : "Unassign Plan"}
          </Button>
        ) : (
          <Button
            fullWidth variant="contained" size="small" disabled={busy}
            onClick={() => onAssign(plan)}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: 2, py: 0.9,
              bgcolor: "#6366f1", "&:hover": { bgcolor: "#4338ca" },
            }}
            startIcon={busy ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : <CheckCircle sx={{ fontSize: 16 }} />}
          >
            {busy ? "Assigning…" : "Assign Plan"}
          </Button>
        )}
      </Box>
    </Card>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PlansTab
// ═════════════════════════════════════════════════════════════════════════════

function PlansTab({ businessid, token }) {
  const [agencyPlans, setAgencyPlans] = useState([]);
  const [bizPlans,    setBizPlans]    = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [assigning,   setAssigning]   = useState(null);
  const [planSubTab,  setPlanSubTab]  = useState(0);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const showToast = (message, severity = "success") => setToast({ open: true, message, severity });
  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  const toArray = (j) => {
    if (Array.isArray(j)) return j;
    if (Array.isArray(j?.data)) return j.data;
    if (Array.isArray(j?.plans)) return j.plans;
    return [];
  };

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const [agencyRes, bizRes] = await Promise.all([
        fetch(`${PAYMENT_BASE}/plans/listwithfeatures?planType=AGENCYMEMBERSHIP&isactive=true&businessid=${businessid}`,
          { headers: { "Content-Type": "application/json" } }),
        fetch(`${PAYMENT_BASE}/plans?planType=BUSINESSPLAN&isactive=true`,
          { headers: { "Content-Type": "application/json" } }),
      ]);
      const agencyJson = agencyRes.ok ? await agencyRes.json() : [];
      const bizJson    = bizRes.ok   ? await bizRes.json()    : [];
      setAgencyPlans(toArray(agencyJson).map((p) => ({ ...p, _planId: p.id ?? p.plan_id ?? p.planid, _source: "agency" })));
      setBizPlans(toArray(bizJson).map((p)    => ({ ...p, _planId: p.id ?? p.plan_id ?? p.planid, _source: "business" })));
    } catch (e) {
      showToast(e.message || "Failed to load plans", "error");
    } finally {
      setLoading(false);
    }
  }, [businessid]);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  const handleAssign = async (plan) => {
    const planId = plan._planId;
    if (!planId || !token) return;
    setAssigning(planId);
    try {
      const res = await fetch(`${PAYMENT_BASE}/plans/${planId}/business`, {
        method: "PATCH",
        headers: { "accept": "application/json", "Content-Type": "application/json", "x-access-token": token },
        body: JSON.stringify({ businessIds: [businessid] }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.message || `HTTP ${res.status}`); }
      showToast(`"${plan.name}" assigned successfully.`, "success");
      await loadPlans();
    } catch (e) { showToast(e.message, "error"); }
    finally { setAssigning(null); }
  };

  const handleUnassign = async (plan) => {
    const planId = plan._planId;
    if (!planId || !token) return;
    setAssigning(planId);
    try {
      const res = await fetch(`${PAYMENT_BASE}/plans/${planId}/business/remove`, {
        method: "PATCH",
        headers: { "accept": "application/json", "Content-Type": "application/json", "x-access-token": token },
        body: JSON.stringify({ businessIds: [businessid] }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.message || `HTTP ${res.status}`); }
      showToast(`"${plan.name}" unassigned successfully.`, "success");
      await loadPlans();
    } catch (e) { showToast(e.message, "error"); }
    finally { setAssigning(null); }
  };

  const currentPlans = planSubTab === 0 ? agencyPlans : bizPlans;

  const countAssigned = (plans) => plans.filter((p) => {
    const bids  = Array.isArray(p.businessids) ? p.businessids : [];
    const bsubs = Array.isArray(p.businessSubscriptions) ? p.businessSubscriptions : [];
    return bids.includes(businessid) || bsubs.some((s) => s.businessid === businessid && s.status !== "CANCELLED");
  }).length;

  const agencyAssigned = countAssigned(agencyPlans);
  const bizAssigned    = countAssigned(bizPlans);

  return (
    <Box>
      <Snackbar open={toast.open} autoHideDuration={4000} onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={closeToast} severity={toast.severity} variant="filled"
          sx={{ borderRadius: 2, fontWeight: 600, fontSize: 13.5, minWidth: 300, boxShadow: "0 8px 24px rgba(0,0,0,0.18)" }}>
          {toast.message}
        </Alert>
      </Snackbar>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          {[
            { label: "Agency Plans",   count: agencyPlans.length, assigned: agencyAssigned, color: "#1d4ed8", bg: "#eff6ff" },
            { label: "Business Plans", count: bizPlans.length,    assigned: bizAssigned,    color: "#9333ea", bg: "#fdf4ff" },
          ].map((tab, i) => (
            <Box key={i} onClick={() => setPlanSubTab(i)} sx={{
              cursor: "pointer", px: 2, py: 1, borderRadius: 2,
              border: "1.5px solid",
              borderColor: planSubTab === i ? tab.color : COLORS.border,
              bgcolor: planSubTab === i ? tab.bg : "transparent",
              transition: "all .15s",
              display: "flex", alignItems: "center", gap: 1,
            }}>
              <Typography sx={{ fontWeight: 700, fontSize: 13, color: planSubTab === i ? tab.color : COLORS.muted }}>
                {tab.label}
              </Typography>
              <Chip size="small" label={tab.count} sx={{
                height: 18, fontSize: 10.5, fontWeight: 700,
                bgcolor: planSubTab === i ? tab.color : COLORS.border,
                color: planSubTab === i ? "#fff" : COLORS.muted,
              }} />
              {tab.assigned > 0 && (
                <Chip size="small" label={`${tab.assigned} assigned`} sx={{
                  height: 18, fontSize: 10, fontWeight: 700,
                  bgcolor: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0",
                }} />
              )}
            </Box>
          ))}
        </Box>
        <Button variant="outlined" size="small"
          startIcon={loading ? <CircularProgress size={12} /> : <Refresh />}
          onClick={loadPlans} disabled={loading}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, borderColor: COLORS.border, color: COLORS.muted }}>
          Refresh
        </Button>
      </Box>

      {loading && currentPlans.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#6366f1" }} />
        </Box>
      ) : currentPlans.length === 0 ? (
        <EmptyState icon="📋" title="No plans found"
          desc={`No active ${planSubTab === 0 ? "agency" : "business"} plans available.`} />
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" }, gap: 2.5 }}>
          {currentPlans.map((plan) => (
            <PlanCard key={plan._planId} plan={plan} businessid={businessid}
              onAssign={handleAssign} onUnassign={handleUnassign}
              assigning={assigning} showActions={planSubTab === 1} />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ExclusionStatePanel
// ═════════════════════════════════════════════════════════════════════════════

function ExclusionStatePanel({ businessid, businessEmail, token, onChanged }) {
  const [state,       setState]       = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [busy,        setBusy]        = useState(false);
  const [error,       setError]       = useState("");
  const [excludeOpen, setExcludeOpen] = useState(false);
  const [includeOpen, setIncludeOpen] = useState(false);

  // Holds intended pre-fill values; consumed by the useEffect below when the dialog opens.
  const prefillRef = useRef(null);

  const [exStages, setExStages] = useState([]);
  const [exReason, setExReason] = useState("");
  const [exExpiry, setExExpiry] = useState("");

  // Whenever the exclude dialog opens, apply whatever prefillRef holds (or clear for fresh).
  useEffect(() => {
    if (!excludeOpen) return;
    const p = prefillRef.current;
    setExStages(p?.stages || []);
    setExReason(p?.reason || "");
    setExExpiry(p?.expiry || "");
    prefillRef.current = null;
  }, [excludeOpen]);

  const fetchState = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await gqlRequest(GQL_EXCLUSION_STATE, { businessid }, token);
      setState(res?.planExpiryBusinessExclusionState || null);
    } catch (e) {
      setError("Could not load exclusion state.");
    } finally {
      setLoading(false);
    }
  }, [businessid, token]);

  useEffect(() => { if (businessid) fetchState(); }, [fetchState]);

  const handleExclude = async () => {
    setBusy(true);
    try {
      await gqlRequest(GQL_EXCLUDE, {
        input: {
          businessid,
          email: businessEmail || "",
          ...(exStages.length ? { stages: exStages } : { stages: [] }),
          ...(exReason.trim() ? { reason: exReason.trim() } : {}),
          ...(exExpiry ? { expiresat: new Date(exExpiry).toISOString() } : {}),
        },
      }, token);
      setExcludeOpen(false);
      setExStages([]); setExReason(""); setExExpiry("");
      await fetchState();
      onChanged?.();
    } catch (e) {
      setError(e?.message || "Exclude failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleInclude = async () => {
    setBusy(true);
    try {
      await gqlRequest(GQL_INCLUDE, { businessid }, token);
      setIncludeOpen(false);
      await fetchState();
      onChanged?.();
    } catch (e) {
      setError(e?.message || "Include failed.");
    } finally {
      setBusy(false);
    }
  };

  const isExcluded = state?.isexcluded;

  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1.5, p: 2,
        borderBottom: "1px solid", borderColor: "divider",
        bgcolor: (t) => alpha(isExcluded ? t.palette.error.main : t.palette.success.main, 0.05),
      }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 1, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: (t) => alpha(isExcluded ? t.palette.error.main : t.palette.success.main, 0.12),
          color: isExcluded ? "error.main" : "success.main",
        }}>
          {isExcluded
            ? <NotificationsOffOutlined sx={{ fontSize: 20 }} />
            : <NotificationsActiveOutlined sx={{ fontSize: 20 }} />}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Expiry Notification Exclusion</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            Control whether this business receives plan expiry emails
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchState} disabled={loading}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
            {loading ? <CircularProgress size={14} /> : <RefreshOutlined sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5, fontSize: 12 }}>{error}</Alert>}

        {loading && !state ? (
          <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={22} /></Box>
        ) : state ? (
          <Stack spacing={2}>
            {/* Status banner */}
            <Box sx={{
              display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
              p: 2, borderRadius: 1.5,
              bgcolor: (t) => alpha(isExcluded ? t.palette.error.main : t.palette.success.main, 0.06),
              border: "1px solid",
              borderColor: (t) => alpha(isExcluded ? t.palette.error.main : t.palette.success.main, 0.2),
            }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                  {isExcluded ? "Currently excluded from notifications" : "Receiving notifications normally"}
                </Typography>
                {isExcluded && state.excludedstages?.length > 0 && (
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 0.5, alignItems: "center" }}>
                    <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>Excluded stages:</Typography>
                    {state.excludedstages.map((s) => (
                      <Chip key={s} size="small" label={STAGE_META[s]?.label || s}
                        color={STAGE_META[s]?.color || "default"}
                        sx={{ fontWeight: 700, fontSize: 10.5, height: 20 }} />
                    ))}
                  </Box>
                )}
                {isExcluded && state.excludedstages?.length === 0 && (
                  <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>All stages excluded</Typography>
                )}
              </Box>
              <Chip size="small" label={isExcluded ? "Excluded" : "Active"}
                color={isExcluded ? "error" : "success"} sx={{ fontWeight: 800, fontSize: 11 }} />
            </Box>

            {/* Active rules */}
            {isExcluded && state.rules?.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>
                  Active Rules
                </Typography>
                <Stack spacing={0.75}>
                  {state.rules.map((rule, i) => (
                    <Box key={rule.exclusionid || i} sx={{
                      p: 1.5, borderRadius: 1.5, border: "1px solid", borderColor: "divider",
                      bgcolor: (t) => alpha(t.palette.text.primary, 0.02),
                    }}>
                      {/* Row 1 — reason + scope badges */}
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.75 }}>
                        <WarningAmberOutlined sx={{ fontSize: 15, color: "warning.main", mt: 0.25, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, flex: 1, minWidth: 0 }}>
                          {rule.reason || "No reason specified"}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                          {/* Scope indicator */}
                          {rule.emaildomain && (
                            <Chip size="small" label={`Domain: ${rule.emaildomain}`}
                              sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#fdf4ff", color: "#9333ea", border: "1px solid #e9d5ff" }} />
                          )}
                          {rule.email && !rule.emaildomain && (
                            <Chip size="small" label={`Email: ${rule.email}`}
                              sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" }} />
                          )}
                          {rule.stage ? (
                            <Chip size="small" label={STAGE_META[rule.stage]?.label || rule.stage}
                              color={STAGE_META[rule.stage]?.color || "default"}
                              sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                          ) : (
                            <Chip size="small" label="All stages"
                              sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#fef2f2", color: "#dc2626", border: "1px solid #fca5a5" }} />
                          )}
                          {rule.source === "HARD_REJECTION" && (
                            <Chip size="small" label="Hard bounce"
                              sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#fef2f2", color: "#dc2626", border: "1px solid #fca5a5" }} />
                          )}
                        </Box>
                      </Box>
                      {/* Row 2 — meta */}
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", pl: "22px" }}>
                        {rule.source && (
                          <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
                            Source: <Box component="span" sx={{ fontWeight: 600, color: "text.secondary" }}>{rule.source}</Box>
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
                          {rule.expiresat
                            ? <>Expires: <Box component="span" sx={{ fontWeight: 600, color: "text.secondary" }}>{fmtDt(rule.expiresat)}</Box></>
                            : "Permanent"}
                        </Typography>
                        {rule.createdat && (
                          <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
                            Added: <Box component="span" sx={{ fontWeight: 600, color: "text.secondary" }}>{fmtDt(rule.createdat)}</Box>
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            <Divider />

            {/* Action buttons */}
            <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
              <Button variant="contained" size="small"
                color={isExcluded ? "warning" : "error"}
                startIcon={isExcluded
                  ? <NotificationsActiveOutlined sx={{ fontSize: 16 }} />
                  : <NotificationsOffOutlined sx={{ fontSize: 16 }} />}
                onClick={() => {
                  if (isExcluded) {
                    setIncludeOpen(true);
                  } else {
                    prefillRef.current = null; // fresh — no pre-fill
                    setExcludeOpen(true);
                  }
                }}
                disabled={busy}
                sx={{ textTransform: "none", fontWeight: 700, fontSize: 13, borderRadius: 1.5, boxShadow: "none" }}>
                {isExcluded ? "Re-include in Notifications" : "Exclude from Notifications"}
              </Button>
            </Stack>
          </Stack>
        ) : null}
      </Box>

      {/* Exclude dialog */}
      <Dialog open={excludeOpen} onClose={() => !busy && setExcludeOpen(false)}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}>
          <NotificationsOffOutlined sx={{ fontSize: 20, color: "error.main" }} />
          {state?.isexcluded ? "Update Exclusion Rules" : "Exclude from Notifications"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13, mb: 2 }}>
            Choose stages to suppress. At least one stage must be selected.
          </DialogContentText>
          {businessEmail && (
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1, mb: 2,
              p: 1.25, borderRadius: 1.5,
              bgcolor: (t) => alpha(t.palette.info.main, 0.06),
              border: "1px solid", borderColor: (t) => alpha(t.palette.info.main, 0.2),
            }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: 0.6 }}>
                Email
              </Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "text.primary", flex: 1 }}>
                {businessEmail}
              </Typography>
            </Box>
          )}
          <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75, color: exStages.length === 0 ? "error.main" : "text.secondary" }}>
            Stages to exclude *
          </Typography>
          <FormGroup sx={{ mb: 2 }}>
            {STAGES.map((s) => (
              <FormControlLabel key={s}
                control={
                  <Checkbox size="small"
                    checked={exStages.includes(s)}
                    onChange={(e) => setExStages((prev) =>
                      e.target.checked ? [...prev, s] : prev.filter((x) => x !== s)
                    )} />
                }
                label={<Typography sx={{ fontSize: 13 }}>{STAGE_META[s]?.label || s}</Typography>} />
            ))}
          </FormGroup>
          {exStages.length === 0 && (
            <Typography sx={{ fontSize: 11.5, color: "error.main", mb: 1.5, mt: -0.5 }}>
              Select at least one stage to continue.
            </Typography>
          )}
          <TextField fullWidth size="small" label="Reason" placeholder="e.g. Renewal in progress"
            value={exReason} onChange={(e) => setExReason(e.target.value)} sx={{ mb: 1.5 }} />
          <TextField fullWidth size="small" label="Exclusion expires at (optional)"
            type="datetime-local" InputLabelProps={{ shrink: true }}
            value={exExpiry} onChange={(e) => setExExpiry(e.target.value)}
            helperText="Leave blank for permanent exclusion" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setExcludeOpen(false)} disabled={busy}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleExclude} disabled={busy || exStages.length === 0}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <NotificationsOffOutlined />}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, boxShadow: "none" }}>
            {busy ? "Saving…" : "Apply Exclusion"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Include dialog */}
      <Dialog open={includeOpen} onClose={() => !busy && setIncludeOpen(false)}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}>
          <LockOpenOutlined sx={{ fontSize: 20, color: "success.main" }} />
          Re-include in Notifications
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>
            This will clear all exclusion rules and resume normal plan expiry notifications.
            {state?.rules?.length > 0 && (
              <Box component="span" sx={{ display: "block", mt: 1, fontWeight: 600 }}>
                {state.rules.length} active rule{state.rules.length !== 1 ? "s" : ""} will be removed.
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setIncludeOpen(false)} disabled={busy}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleInclude} disabled={busy}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <NotificationsActiveOutlined />}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, boxShadow: "none" }}>
            {busy ? "Applying…" : "Re-enable Notifications"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// NotificationLog
// ═════════════════════════════════════════════════════════════════════════════

function StageChip({ stage }) {
  const m = STAGE_META[stage] || { label: stage, color: "default" };
  return <Chip size="small" label={m.label} color={m.color} sx={{ fontWeight: 700, fontSize: 10.5, height: 20 }} />;
}

function NotifStatusChip({ status }) {
  const m = STATUS_META[status] || { label: status, color: "default", Icon: InfoOutlined };
  const { Icon } = m;
  return (
    <Chip size="small" label={m.label} color={m.color}
      icon={<Icon sx={{ fontSize: "12px !important" }} />}
      sx={{ fontWeight: 700, fontSize: 10.5, height: 20, "& .MuiChip-icon": { ml: 0.5 } }} />
  );
}

function NotificationLog({ businessid, token }) {
  const [rows,         setRows]         = useState([]);
  const [count,        setCount]        = useState(0);
  const [loading,      setLoading]      = useState(false);
  const [page,         setPage]         = useState(1);
  const [pageSize,     setPageSize]     = useState(25);
  const [filterStage,  setFilterStage]  = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filtersOpen,  setFiltersOpen]  = useState(false);
  const [expandedRow,  setExpandedRow]  = useState(null);

  const load = useCallback(async (pg = 1, ps = pageSize, stage = filterStage, status = filterStatus) => {
    setLoading(true);
    try {
      const res = await gqlRequest(GQL_NOTIFICATIONS, {
        filter: {
          businessid: businessid || null,
          stage:  stage  || null,
          status: status || null,
          pagenumber: pg,
          pagesize:   ps,
          fromdate: null, todate: null, search: null,
        },
      }, token);
      const d = res?.planExpiryNotifications;
      setRows(d?.rows || []);
      setCount(d?.count || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [businessid, token, pageSize, filterStage, filterStatus]);

  useEffect(() => { load(page, pageSize, filterStage, filterStatus); }, [page, pageSize, filterStage, filterStatus]);

  const pageCount = Math.max(1, Math.ceil(count / pageSize));
  const toggleBtnSx = {
    "& .MuiToggleButton-root": {
      textTransform: "none", fontWeight: 600, fontSize: 11.5,
      px: 1.5, py: 0.5, borderRadius: "8px !important",
      border: "1px solid !important", borderColor: "divider !important", mx: 0.25,
      "&.Mui-selected": { bgcolor: "primary.main", color: "#fff", borderColor: "primary.main !important" },
    },
  };

  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 1, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: (t) => alpha(t.palette.primary.main, 0.1), color: "primary.main",
        }}>
          <NotificationsActiveOutlined sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Notification Log</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {count > 0 ? `${count} notification${count !== 1 ? "s" : ""}` : "No notifications yet"}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined"
            startIcon={<FilterListOutlined sx={{ fontSize: 15 }} />}
            onClick={() => setFiltersOpen((p) => !p)}
            endIcon={filtersOpen ? <KeyboardArrowUp sx={{ fontSize: 15 }} /> : <KeyboardArrowDown sx={{ fontSize: 15 }} />}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: 12, borderRadius: 1.5 }}>
            Filter
            {(filterStage || filterStatus) && (
              <Chip size="small" label={[filterStage, filterStatus].filter(Boolean).length}
                sx={{ ml: 0.5, height: 16, fontSize: 10, bgcolor: "primary.main", color: "#fff", fontWeight: 800 }} />
            )}
          </Button>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => load(page, pageSize, filterStage, filterStatus)} disabled={loading}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              {loading ? <CircularProgress size={14} /> : <RefreshOutlined sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Filter bar */}
      <Collapse in={filtersOpen}>
        <Box sx={{
          px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.text.primary, 0.02),
          display: "flex", gap: 2.5, flexWrap: "wrap", alignItems: "center",
        }}>
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.disabled", mb: 0.5, textTransform: "uppercase", letterSpacing: 0.6 }}>Stage</Typography>
            <ToggleButtonGroup size="small" exclusive value={filterStage}
              onChange={(_, v) => { setFilterStage(v); setPage(1); }} sx={toggleBtnSx}>
              <ToggleButton value={null}>All</ToggleButton>
              {STAGES.map((s) => <ToggleButton key={s} value={s}>{STAGE_META[s]?.label || s}</ToggleButton>)}
            </ToggleButtonGroup>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.disabled", mb: 0.5, textTransform: "uppercase", letterSpacing: 0.6 }}>Status</Typography>
            <ToggleButtonGroup size="small" exclusive value={filterStatus}
              onChange={(_, v) => { setFilterStatus(v); setPage(1); }} sx={toggleBtnSx}>
              <ToggleButton value={null}>All</ToggleButton>
              {STATUSES.map((s) => <ToggleButton key={s} value={s}>{STATUS_META[s]?.label || s}</ToggleButton>)}
            </ToggleButtonGroup>
          </Box>
          {(filterStage || filterStatus) && (
            <Button size="small" onClick={() => { setFilterStage(null); setFilterStatus(null); setPage(1); }}
              sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, alignSelf: "flex-end" }}>
              Clear filters
            </Button>
          )}
        </Box>
      </Collapse>

      {/* Table */}
      <Box sx={{ position: "relative" }}>
        {loading && (
          <Box sx={{
            position: "absolute", inset: 0, zIndex: 2,
            display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.65)", minHeight: 200,
          }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {rows.length === 0 && !loading ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <NotificationsOffOutlined sx={{ fontSize: 36, color: "text.disabled", mb: 1 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.secondary" }}>No notifications found</Typography>
            <Typography sx={{ fontSize: 12, color: "text.disabled", mt: 0.5 }}>
              {filterStage || filterStatus ? "Try clearing the filters." : "No plan expiry emails have been sent yet."}
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Stage</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Sent at</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Plan end date</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, width: 36 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const open     = expandedRow === row.notificationid;
                  const hasError = row.status === "FAILED" && row.errormessage;
                  return (
                    <>
                      <TableRow key={row.notificationid} hover
                        sx={{ cursor: hasError ? "pointer" : "default" }}
                        onClick={() => hasError && setExpandedRow(open ? null : row.notificationid)}>
                        <TableCell><StageChip stage={row.stage} /></TableCell>
                        <TableCell><NotifStatusChip status={row.status} /></TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 12, color: COLORS.textSub }}>{fmtDt(row.sentat)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 12, color: COLORS.textSub }}>{fmtDt(row.planenddate)}</Typography>
                        </TableCell>
                        <TableCell>
                          {hasError && (
                            <Tooltip title="View error">
                              <IconButton size="small" sx={{ p: 0.25 }}>
                                {open ? <KeyboardArrowUp sx={{ fontSize: 16 }} /> : <KeyboardArrowDown sx={{ fontSize: 16 }} />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                      {hasError && (
                        <TableRow key={`${row.notificationid}-err`}>
                          <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                            <Collapse in={open}>
                              <Box sx={{
                                mx: 1.5, mb: 1, p: 1.25, borderRadius: 1,
                                bgcolor: (t) => alpha(t.palette.error.main, 0.06),
                                border: "1px solid", borderColor: (t) => alpha(t.palette.error.main, 0.2),
                              }}>
                                <Typography sx={{ fontSize: 11.5, color: "error.main", fontWeight: 600, mb: 0.25 }}>Error</Typography>
                                <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontFamily: "monospace" }}>
                                  {row.errormessage}
                                </Typography>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Pagination */}
      {count > 0 && (
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 2, py: 1.5, borderTop: "1px solid", borderColor: "divider",
          flexWrap: "wrap", gap: 1,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>Rows</Typography>
            <TextField select size="small" value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              sx={{ width: 72, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}>
              {PAGE_SIZE_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>of {count}</Typography>
          </Box>
          <Pagination count={pageCount} page={page} onChange={(_, v) => setPage(v)}
            size="small" shape="rounded" showFirstButton showLastButton
            sx={{ "& .MuiPaginationItem-root": { borderRadius: 1.5, fontWeight: 600 } }} />
        </Box>
      )}
    </Box>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PlanExpiryTab — combines exclusion panel + notification log
// ═════════════════════════════════════════════════════════════════════════════

function PlanExpiryTab({ businessid, businessEmail, token }) {
  return (
    <Stack spacing={2.5}>
      <ExclusionStatePanel businessid={businessid} businessEmail={businessEmail} token={token} />
      <NotificationLog    businessid={businessid} token={token} />
    </Stack>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Main page — 3 tabs all managed here
// ═════════════════════════════════════════════════════════════════════════════

export default function UsageListPage() {
  const { businessid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();

  const biz = location.state?.biz || { entityid: businessid };

  const [activeTab, setActiveTab] = useState(0);

  // ── Usage tab ──────────────────────────────────────────────────────────────
  const [usages,  setUsages]  = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const loadUsages = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const data = await gqlRequest(GET_BUSINESS_USAGES, { businessid }, token);
      setUsages(data.businessusagesbybusiness || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [businessid, token]);

  useEffect(() => { loadUsages(); }, [loadUsages]);

  return (
    <PageWrapper>
      {/* Back + Business identity */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate("/businesses")}
          sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 1.5 }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <BizAvatar biz={biz} size={44} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            {biz.businessname || "Business"}
          </Typography>
          <Typography variant="caption" sx={{ color: COLORS.muted }}>
            ID: {businessid}{biz.email && ` • ${biz.email}`}
          </Typography>
        </Box>
        {activeTab === 0 && (
          <Button
            variant="contained" startIcon={<Add />}
            onClick={() => navigate(`/businesses/${businessid}/usage/create`, { state: { biz } })}
            sx={{ bgcolor: COLORS.accent, "&:hover": { bgcolor: "#4338ca" }, borderRadius: 2, textTransform: "none", fontWeight: 700 }}
          >
            Create Usage
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 13.5, minWidth: 120 },
            "& .Mui-selected": { color: `${COLORS.accent} !important` },
            "& .MuiTabs-indicator": { bgcolor: COLORS.accent },
          }}
        >
          <Tab label="Usage Records" />
          <Tab label="Plans" />
          <Tab
            label="Expiry Notifications"
            icon={<NotificationsNoneOutlined sx={{ fontSize: 16 }} />}
            iconPosition="end"
            sx={{ gap: 0.5 }}
          />
        </Tabs>
      </Box>

      {/* Tab 0 — Usage Records */}
      {activeTab === 0 && (
        <>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>{error}</Alert>}

          <PageHeader
            title="Usage Records"
            subtitle={`${usages.length} record${usages.length !== 1 ? "s" : ""} found`}
            action={
              <Button variant="outlined" size="small" startIcon={<Refresh />}
                onClick={loadUsages} disabled={loading}
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, borderColor: COLORS.border, color: COLORS.muted }}>
                Refresh
              </Button>
            }
          />

          {!loading && usages.length === 0 ? (
            <EmptyState icon="📭" title="No usage records"
              desc="This business has no usage records yet. Create one to get started."
              action={
                <Button variant="contained"
                  onClick={() => navigate(`/businesses/${businessid}/usage/create`, { state: { biz } })}
                  sx={{ bgcolor: COLORS.accent, borderRadius: 2, textTransform: "none", fontWeight: 700 }}>
                  <Add sx={{ mr: 0.5 }} /> Create First Usage
                </Button>
              }
            />
          ) : (
            <SectionCard>
              <Box sx={{ position: "relative" }}>
                {loading && (
                  <Box sx={{
                    position: "absolute", inset: 0, zIndex: 2,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    bgcolor: "rgba(255,255,255,0.6)", minHeight: TABLE_MAX_HEIGHT,
                  }}>
                    <CircularProgress sx={{ color: COLORS.accent }} />
                  </Box>
                )}
                <TableContainer sx={{ overflowX: "auto", maxHeight: TABLE_MAX_HEIGHT, overflowY: "auto" }}>
                  <Table size="small" stickyHeader sx={{ whiteSpace: "nowrap" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Action</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell>Start Date</TableCell>
                        <TableCell>End Date</TableCell>
                        <TableCell>Status</TableCell>
                        {FEATURE_FIELDS.map((f) => (
                          <TableCell key={f.label}>{f.label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usages.map((u) => (
                        <TableRow key={u.businessusageid} hover>
                          <TableCell>
                            <Button size="small" startIcon={<Edit />}
                              disabled={!u.businessusageid}
                              onClick={() => navigate(`/businesses/${businessid}/usage/${u.businessusageid}/edit`, { state: { biz, usage: u } })}
                              sx={{ textTransform: "none", fontWeight: 600, fontSize: 12, color: COLORS.accent, borderColor: COLORS.accent, borderRadius: 1.5 }}
                              variant="outlined">
                              Edit
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                              <PlanChip type={u.plantype} />
                              {u.isfreeplan   && <Chip size="small" label="Free" sx={{ bgcolor: COLORS.warnLight, color: COLORS.warn, fontSize: 11 }} />}
                              {u.isfullaccess && <Chip size="small" label="Full" sx={{ bgcolor: "#fdf4ff", color: "#9333ea", fontSize: 11 }} />}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {fmtTimestamp(u.startdate) || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {fmtTimestamp(u.enddate) || "No end"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small"
                              label={isUsageActive(u.enddate) ? "Active" : "Expired"}
                              sx={{
                                fontSize: 11, fontWeight: 700,
                                bgcolor:     isUsageActive(u.enddate) ? "#f0fdf4" : "#fef2f2",
                                color:       isUsageActive(u.enddate) ? "#15803d" : "#dc2626",
                                border: "1px solid",
                                borderColor: isUsageActive(u.enddate) ? "#bbf7d0" : "#fca5a5",
                              }} />
                          </TableCell>
                          {FEATURE_FIELDS.map((f) => {
                            const unlimited = f.unlimitedKey ? !!u[f.unlimitedKey] : false;
                            return (
                              <TableCell key={f.label}>
                                <Typography variant="body2" sx={{
                                  color: unlimited ? COLORS.success : COLORS.muted,
                                  fontWeight: unlimited ? 700 : 400,
                                }}>
                                  {unlimited ? "Unlimited" : (u[f.limitKey] ?? "—")}
                                </Typography>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </SectionCard>
          )}
        </>
      )}

      {/* Tab 1 — Plans */}
      {activeTab === 1 && (
        <PlansTab businessid={businessid} token={token} />
      )}

      {/* Tab 2 — Expiry Notifications */}
      {activeTab === 2 && (
        <PlanExpiryTab businessid={businessid} businessEmail={biz.email || ""} token={token} />
      )}
    </PageWrapper>
  );
}